<script setup lang="ts">
import { z } from 'zod'
import type { invoiceSchema } from '@/features/invoices/schemas'
import CardContainer from '@/components/Card/card-container.vue'
import CardHeader from '@/components/Card/card-header.vue'
import CardContent from '@/components/Card/card-content.vue'
import InputText from '@/components/input-text.vue'
import { ChargeType } from '@/api/billing/constants'
import IconChanged from '@/shared/components/IconChanged.vue'
import InputNumber from '@/components/input-number.vue'
import { formatNumberToPercentage, formatToFractionalCurrency, formatToMainCurrency } from '@/helpers/formatting'
import DropdownItem from '@/components/dropdown-item.vue'
import DropDownContext from '@/components/dropdown-context.vue'
import { computed, reactive, ref } from 'vue'
import AddRecurringChargeModal from '@/views/billing/invoicing/add-recurring-charge-modal.vue'
import { useModalState } from '@/shared/composables/useModalState'
import useLoader from '@/composables/loader'
import { useRemoveBillingCharge } from '@/composables/billing/invoices'
import useNotifications from '@/composables/notifications'
import ModalVariantConfirmation from '@/components/modal/modal-variant-confirmation.vue'
import InputCheckbox from '@/components/input-checkbox.vue'
import InputToggleGroup from '@/components/input-toggle-group.vue'
import IconHelp from '@/shared/components/IconHelp.vue'
import { chargeTypeDisplayNames } from '@/features/invoices/constants'
import { calculateBillingLineItemTotalInRands, roundBillingCents, roundBillingRands, roundBillingQuantity, formatRands, Logger, formatNumber } from '@/shared'
import { watchDebounced } from '@vueuse/core'

type Category = z.infer<typeof invoiceSchema>['categories'][number]
type Charge = Category['charges'][number]
type TrackedChange<TItem> = { id: number, values: Partial<TItem> }

const valuesModifiedTooltipText = 'This value was edited and will not align with the monthly solar report totals.'
const props = withDefaults(
  defineProps<{
    assetCode: string
    categories: Category[]
    editableChargeTypes?: ChargeType[]
    removableChargeTypes?: ChargeType[]
    capabilities?: ('add-charge'|'hide-charges')[]
    totalExcludingTax: number
    totalIncludingTax: number
    tax: number
    taxPercentage: number
    summaryType?: 'all'|'category'
  }>(),
  {
    capabilities: () => [],
    editableChargeTypes: () => [],
    removableChargeTypes: () => [],
    summaryType: 'all'
  }
)

const { wrap } = useLoader()
const { addSuccess, addError } = useNotifications()
const { mutateAsync: removeBillingCharge } = useRemoveBillingCharge(() => props.assetCode)
const { isOpen: chargeModalIsOpen, close: closeChargeModal, open: openChargeModal } = useModalState()

const confirmChargeDeletion = ref<Charge>()
const updatingChargeIndex = ref<[number, number]>()
const tracked = reactive<{ charges: TrackedChange<Charge>[], categories: TrackedChange<Category>[] }>({ charges: [], categories: [] })
const currentRecurringCharge = reactive<{ categoryId?: number, chargeId?: number }>({ })
let inputBlurTimeout: number|undefined = undefined

const chargesWithChanges = computed(
  () => props.categories.reduce(
    (reduced, category) => {
      reduced[category.id] = category.charges.map(
        charge => ({
          ...charge,
          ...(tracked.charges.find(x => x.id === charge.id) ?? { values: { } }).values
        })
      )

      return reduced
    },
    { } as Record<number, Charge[]>
  )
)

watchDebounced(
  chargesWithChanges,
  charges => {
    Logger.trace(
      `Charges were updated and totals have been recalculated`,
      {
        totals: Object.values(charges).flat().map(
          charge => ({
            description: charge.description,
            quantity: roundBillingQuantity(charge.quantity),
            rateInCents: roundBillingCents(charge.rateInCents),
            total: calculateBillingLineItemTotalInRands({ quantity: charge.quantity, rateInCents: charge.rateInCents })
          })
        )
      }
    )
  },
  { debounce: 500 }
)

const categoryTotalsWithChangesByCategoryId = computed(
  () => {
    const x: Record<number, number> = { }

    for (const categoryId in chargesWithChanges.value) {
      x[categoryId] = chargesWithChanges.value[categoryId].reduce(
        (total, { quantity, rateInCents }) => total + calculateBillingLineItemTotalInRands({ quantity, rateInCents }),
        0
      )
    }

    return x
  },
  { } as Record<number, number>
)

function trackChange<TItem>(collection: keyof typeof tracked, id: number, newValues: Partial<TItem>) {
  const copied = [...tracked[collection]]
  const modifiedIndex = copied.findIndex(x => x.id === id)

  if (modifiedIndex > -1) {
    copied[modifiedIndex].values = { ...copied[modifiedIndex].values, ...newValues }
  } else {
    copied.push({ id, values: newValues })
  }

  tracked[collection] = copied
}

function blurEditableField() {
  inputBlurTimeout = window.setTimeout(
    () => updatingChargeIndex.value = undefined
  )
}

function focusEditableField() {
  clearTimeout(inputBlurTimeout)
}

function isUpdatingCharge(categoryIndex: number, chargeIndex: number) {
  return updatingChargeIndex.value?.[0] === categoryIndex && updatingChargeIndex.value?.[1] === chargeIndex
}

function addNewCharge(categoryId: number) {
  currentRecurringCharge.chargeId = undefined
  currentRecurringCharge.categoryId = categoryId

  openChargeModal()
}

function editExistingCharge(charge: Charge, categoryIndex: number) {
  if (props.capabilities.includes('add-charge')) {
    currentRecurringCharge.chargeId = charge.id
    currentRecurringCharge.categoryId = undefined
    updatingChargeIndex.value = undefined
    openChargeModal()
  } else {
    currentRecurringCharge.chargeId = undefined
    currentRecurringCharge.categoryId = undefined
    updatingChargeIndex.value = [categoryIndex, props.categories[categoryIndex].charges.findIndex(x => x.id === charge.id)]
  }
}

function handleChargeDeletion(charge: Charge) {
  const chargeId = charge.id

  // Do nothing if we don't have an ID.
  if (chargeId === undefined || chargeId === 0) {
    return
  }

  wrap(
    () => removeBillingCharge(chargeId)
  ).then(
    () => {
      addSuccess({
        title: 'Recurring charge removed.',
        allowHtml: true,
        message: `Recurring charge <b>${charge.description}</b> has been removed successfully.`
      })

      confirmChargeDeletion.value = undefined
    },
    () => addError({
      title: 'Recurring charge not removed.',
      allowHtml: true,
      message: `Recurring charge <b>${charge.description}</b> could not be successfully removed. Please reload the page and try again.`
    })
  )
}

defineExpose({
  changes() {
    return {
      categories: [...tracked.categories],
      charges: [...tracked.charges],
    }
  },
  clearChanges() {
    tracked.categories = []
    tracked.charges = []
  }
})
</script>

<template>
  <CardContainer :class="$style.category" v-for="(category, categoryIndex) in categories" :key="category.id">
    <CardHeader uppercase>
      <span>{{ category.name }}</span>
      <span v-if="category.features.includes('hide')">
        <InputToggleGroup :options="[{ value: true, text: 'Show' }, { value: false, text: 'Hide' }]"
                          :selected="!category.hidden"
                          @update:selected="trackChange('categories', category.id, { hidden: !$event })" />
        <IconHelp class="ms-2" :tooltip="`Show or hide this\nsection on the invoice.`" />
      </span>
    </CardHeader>
    <CardContent>
      <table class="table table-no-bottom-border">
        <thead>
          <tr>
            <th>Name</th>
            <th>Billing</th>
            <th class="text-end">Rate</th>
            <th class="text-end">Quantity</th>
            <th class="text-end">Total (R)</th>
            <th v-if="capabilities.includes('hide-charges') && category.features.includes('hide-charges')">Hide</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(charge, chargeIndex) in chargesWithChanges[category.id]" :key="charge.id">
            <td>
              <InputText v-if="isUpdatingCharge(categoryIndex, chargeIndex) && [ChargeType.OnceOff, ChargeType.Recurring].includes(charge.chargeType)"
                         :value="charge.description"
                         @update:value="trackChange('charges', charge.id, { description: $event })"
                         @focus="focusEditableField"
                         @blur="blurEditableField" />
              <span v-else>{{ charge.description }}</span>
            </td>
            <td>{{ chargeTypeDisplayNames[charge.chargeType] }}</td>
            <td class="text-end">{{ formatRands(roundBillingRands(charge.rateInCents / 100), { showSymbol: true }) }}</td>
            <td class="text-end">
              <InputNumber v-if="isUpdatingCharge(categoryIndex, chargeIndex)"
                           :value="charge.quantity"
                           inline
                           align="right"
                           @update:value="trackChange('charges', charge.id, { quantity: roundBillingQuantity($event) })"
                           @focus="focusEditableField"
                           @blur="blurEditableField">

                <template #suffix v-if="charge.quantityUnitOfMeasure != null">
                  <span class="text-muted">{{ charge.quantityUnitOfMeasure }}</span>
                </template>
              </InputNumber>

              <span v-else>{{ formatNumber(roundBillingQuantity(charge.quantity)) }} {{ charge.quantityUnitOfMeasure }}</span>
              <IconChanged class="ms-1" v-if="charge.isQuantityModified && !isUpdatingCharge(categoryIndex, chargeIndex)" :tooltip="valuesModifiedTooltipText" />
            </td>
            <td class="text-end">{{
              formatRands(calculateBillingLineItemTotalInRands({ quantity: charge.quantity, rateInCents: charge.rateInCents }), { maxScale: 2 })
            }}</td>
            <td style="width: 1px;" v-if="capabilities.includes('hide-charges') && category.features.includes('hide-charges')">
              <InputCheckbox :disabled="charge.id < 1" :checked="charge.hidden" @update:checked="trackChange('charges', charge.id, { hidden: $event })" />
            </td>
            <td style="width: 1px;">
              <DropDownContext v-if="editableChargeTypes.includes(charge.chargeType) || removableChargeTypes.includes(charge.chargeType)">
                <DropdownItem v-if="editableChargeTypes.includes(charge.chargeType)" @click="editExistingCharge(charge, categoryIndex)">Edit</DropdownItem>
                <DropdownItem v-if="removableChargeTypes.includes(charge.chargeType)" @click="confirmChargeDeletion = charge">Delete</DropdownItem>
              </DropDownContext>
            </td>
          </tr>
        </tbody>
      </table>

      <a v-if="capabilities.includes('add-charge')" href="javascript:void(0)" @click="addNewCharge(category.id)" :class="$style['new-charge']">+ New Charge</a>

      <div v-if="summaryType === 'all' && categoryIndex === categories.length - 1" :class="$style.summary">
        <span class="text-end">Subtotal (Excl. VAT)</span>
        <span>{{ formatToMainCurrency(totalExcludingTax) }}</span>
        <span class="text-end">VAT ({{ formatNumberToPercentage(taxPercentage / 100) }})</span>
        <span>{{ formatToMainCurrency(tax) }}</span>
        <span class="text-end">Total (Incl. VAT)</span>
        <span>{{ formatToMainCurrency(totalIncludingTax) }}</span>
      </div>

      <div v-if="summaryType === 'category'" :class="$style.summary">
        <span class="text-end">Subtotal</span>
        <span>{{ formatToMainCurrency(categoryTotalsWithChangesByCategoryId[category.id]) }}</span>
      </div>
    </CardContent>
  </CardContainer>

  <AddRecurringChargeModal v-if="chargeModalIsOpen"
                           @close="closeChargeModal"
                           :asset-code="assetCode"
                           :category-id="currentRecurringCharge.categoryId"
                           :billing-charge-id="currentRecurringCharge.chargeId" />

  <ModalVariantConfirmation v-if="confirmChargeDeletion"
                            @reject="confirmChargeDeletion = undefined"
                            @confirm="handleChargeDeletion(confirmChargeDeletion)">
    <p>
      Are you sure you would like to delete the charge <b>{{ confirmChargeDeletion.description }}</b>
      for <b>{{ formatToMainCurrency(confirmChargeDeletion.rateInCents / 100) }}</b>
      from the invoice charges section of this invoice template?
    </p>
  </ModalVariantConfirmation>
</template>

<style module lang="scss">
@import "@/assets/variables";

.summary {
  background: #eee;
  font-weight: 700;
  padding: 20px 20px 20px 64.5%;
  border-bottom-left-radius: #{$card-border-radius};
  border-bottom-right-radius: #{$card-border-radius};
  display: grid;
  grid-template-columns: 167px 1fr;
  column-gap: 78px;
  margin: #{$card-spacer-y} #{-1 * $card-spacer-x} #{-1 * $card-spacer-y};
}

.category:not(:first-child) {
  margin-top: 27px;
}

.new-charge {
  text-decoration: none;
}
</style>
